import React, { Component } from "react";
import PropTypes from "prop-types";
import { ContactService } from "@services";
import utils from "@utils";
import { Image, ScrollDetect, AddContactModal } from "../../../../components";

const displayCount = 10;

class ContactsTab extends Component {
	constructor(props) {
		super(props);
		this.state = {
			showAddContactModal: false,
			contact: {},
		};
	}

	componentDidMount() {
		this.getContacts();
	}

	/** Retrieves a paginated list of contacts for the client, also updating the list of available contacts in other tabs as well */
	getContacts = () => {
		const { lang } = global;
		const {
			client,
			contacts: { pageNumber, ...contacts },
			updateContacts,
		} = this.props;

		ContactService.getContactsByClientId(client.Id, { displayCount, pageNumber })
			.then(({ data: { ItemList } }) => {
				updateContacts({
					data: pageNumber > 1 ? [...contacts.data, ...ItemList] : ItemList,
					pageNumber: pageNumber + 1,
					hasMore: ItemList.length >= displayCount,
				});
			})
			.catch(() => {
				updateContacts({ data: [], pageNumber: 1, hasMore: false });
				utils.notify({
					type: "error",
					message: lang.t("Error loading contacts"),
				});
			});
	};

	openAddContactModal = () => {
		this.setState({
			showAddContactModal: true,
		});
	};

	closeAddContactModal = () => {
		const { updateContacts } = this.props;
		this.setState(
			{
				showAddContactModal: false,
				contact: {},
			},
			() => {
				updateContacts({ pageNumber: 1 }, () => this.getContacts());
			},
		);
	};

	onEditContact = contactEdit => {
		this.setState({
			showAddContactModal: true,
			contact: contactEdit,
		});
	};

	/**
	 * Deletes a contact record and updates the list of available contacts in other tabs as well
	 * @param {Object} contactDel The contact to delete
	 */
	onDeleteContact = contactDel => {
		const { lang } = global;
		const { updateContacts, contacts } = this.props;
		ContactService.delete(contactDel.Id)
			.then(() => {
				updateContacts({
					data: contacts.data.filter(contact => contact.Id !== contactDel.Id),
				});
				utils.notify({
					type: "success",
					message: lang.t("Contact successfully deleted"),
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Something went wrong"),
				});
			});
	};

	render() {
		const { lang } = global;
		const { client, contacts } = this.props;
		const { showAddContactModal, contact } = this.state;

		return (
			<div id="client-view-contacts">
				<button
					type="button"
					aria-label={lang.t("Add Client Contact")}
					className="btn btn-link client-view-add-btn"
					onClick={() => this.openAddContactModal("client-contact")}
					title={lang.t("Add Client Contact")}
				>
					<i className="rkf rkf-plus text-primary" />
				</button>

				<div
					className="col-xs-12"
					id="clients-view-contacts-table"
				>
					<div className="row">
						<div
							id="clients-view-contacts-table-body"
							className="col-xs-12"
						>
							<For
								each="item"
								index="index"
								of={contacts.data}
							>
								<div
									role="row"
									tabIndex={index}
									className="row"
									key={`client_view_contact_item_${index}`}
								>
									<div className="col-sm-4 col-xs-12">
										<div className="client-view-contact-image">
											<Image
												src={item.ContactPhotoUrl || ""}
												fallbackSrc="avatar"
											/>
											<div>
												<div>
													<strong>{`${item.FirstName} ${item.LastName}`}</strong>
												</div>
												<div>{`${item.City} ${item.State ? `, ${item.State}` : ""}`}</div>
												<If
													condition={item.Id === client?.PrimaryContactId}
												>
													<p>{lang.t("Primary Contact")}</p>
												</If>
											</div>
										</div>
									</div>

									<div className="col-sm-5 col-xs-6">
										<div className="row">
											<div className="col-sm-5 col-xs-12">
												<a
													className="btn btn-link"
													hidden={!item.EmailAddress}
													href={`mailto:${item.EmailAddress}`}
													target="_blank"
													rel="noopener noreferrer"
												>
													<i className="rkf rkf-email" />
													{lang.t("EMail")}
												</a>
											</div>

											<div className="col-sm-7 col-xs-12">
												<a
													className="btn btn-link"
													hidden={!item.MobilePhone}
													href={`tel:${item.MobilePhone}`}
													target="_blank"
													rel="noopener noreferrer"
												>
													<i className="rkf rkf-phone" />
													{utils.formatPhoneNumber(item.MobilePhone)}
												</a>
											</div>
										</div>
									</div>

									<div className="col-sm-3 col-xs-6 text-right">
										<button
											type="button"
											className="btn btn-link client-view-round-button btn-edit"
											name="edit-contact"
											onClick={() => this.onEditContact(item)}
											title={lang.t("Edit Contact")}
										>
											<i className="rkf rkf-pencil" />
										</button>
										<button
											type="button"
											className="btn btn-link client-view-round-button btn-delete"
											name="delete-contact"
											onClick={() => this.onDeleteContact(item)}
											title={lang.t("Delete Contact")}
										>
											<i className="rkf rkf-trash" />
										</button>
									</div>
								</div>
							</For>
							<If condition={contacts.hasMore}>
								<ScrollDetect
									className="text-center"
									onVisible={this.getContacts}
								>
									<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
								</ScrollDetect>
							</If>
						</div>
					</div>
				</div>

				<AddContactModal
					showModal={showAddContactModal}
					closeModal={this.closeAddContactModal}
					client={client}
					clientId={client.Id}
					contact={contact}
				/>
			</div>
		);
	}
}

ContactsTab.propTypes = {
	contacts: PropTypes.object.isRequired,
	client: PropTypes.object,
	updateContacts: PropTypes.func.isRequired,
};

ContactsTab.defaultProps = {
	client: {},
};

export default ContactsTab;
